import React, { useState, useEffect } from 'react';
import { userAPI, branchAPI, planAPI } from '../../services/api';
import { useAuthStore, useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import { Users as UsersIcon, Plus, Edit, Trash2, X, CheckCircle, XCircle, Clock, Wifi } from 'lucide-react';
import './Users.css';
import { formatDate } from '../../utils/dateFormat';

const Users = () => {
    const admin = useAuthStore((state) => state.admin);
    const selectedBranch = useBranchStore((state) => state.selectedBranch);
    const [users, setUsers] = useState([]);
    const [branches, setBranches] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);

    const [formData, setFormData] = useState({
        name: '',
        mobile: '',
        email: '',
        password: '',
        branchId: '',
        planId: '',
        status: 'active'
    });

    useEffect(() => {
        fetchUsers();
        fetchBranches();
        if (selectedBranch) fetchPlans();
    }, [selectedBranch]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await userAPI.getAll(selectedBranch?._id);
            setUsers(response.data.data.users || []);
        } catch (error) {
            toast.error('Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    const fetchBranches = async () => {
        try {
            const response = await branchAPI.getAll();
            setBranches(response.data.data.branches || []);
        } catch (error) {
            console.error('Failed to fetch branches:', error);
        }
    };

    const fetchPlans = async () => {
        try {
            const response = await planAPI.getAll(selectedBranch._id);
            setPlans(response.data.data.plans || []);
        } catch (error) {
            console.error('Failed to fetch plans:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...formData };
            if (editingUser && !payload.password) {
                delete payload.password;
            }

            if (editingUser) {
                await userAPI.update(editingUser._id, payload);
                toast.success('User updated successfully');
            } else {
                await userAPI.create(payload);
                toast.success('User created successfully');
            }

            setShowModal(false);
            setEditingUser(null);
            resetForm();
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save user');
        }
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setFormData({
            name: user.name,
            mobile: user.mobile,
            email: user.email,
            password: '',
            branchId: user.branchId?._id || '',
            planId: user.planId?._id || '',
            status: user.status
        });
        setShowModal(true);
    };

    const handleDelete = (userId) => {
        setUserToDelete(userId);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!userToDelete) return;
        try {
            await userAPI.delete(userToDelete);
            toast.success('User deleted successfully');
            fetchUsers();
            setShowDeleteModal(false);
            setUserToDelete(null);
        } catch (error) {
            toast.error('Failed to delete user');
        }
    };

    const handleNewUser = () => {
        setEditingUser(null);
        resetForm();
        setShowModal(true);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            mobile: '',
            email: '',
            password: '',
            branchId: '',
            planId: '',
            status: 'active'
        });
    };

    // Filter users
    const filteredUsers = users.filter(user => {
        const matchesSearch =
            user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.mobile?.includes(searchTerm);

        const matchesStatus = filterStatus === 'all' || user.status === filterStatus;

        return matchesSearch && matchesStatus;
    });

    // Calculate stats
    const stats = {
        total: users.length,
        active: users.filter(u => u.status === 'active').length,
        inactive: users.filter(u => u.status === 'inactive').length,
        expired: users.filter(u => u.status === 'expired').length,
    };

    return (
        <div className="users-page">
            {/* Header */}
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <UsersIcon className="title-icon" />
                            WiFi Users
                        </h1>
                        <p className="page-subtitle-modern">
                            Manage WiFi users and their access plans
                        </p>
                    </div>

                    <button onClick={handleNewUser} className="btn-primary-modern">
                        <Plus size={20} />
                        New User
                    </button>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-icon blue">
                        <UsersIcon size={24} />
                    </div>
                    <div className="stat-details">
                        <div className="stat-value">{stats.total}</div>
                        <div className="stat-label">Total Users</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon green">
                        <CheckCircle size={24} />
                    </div>
                    <div className="stat-details">
                        <div className="stat-value">{stats.active}</div>
                        <div className="stat-label">Active Users</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon red">
                        <XCircle size={24} />
                    </div>
                    <div className="stat-details">
                        <div className="stat-value">{stats.inactive}</div>
                        <div className="stat-label">Inactive Users</div>
                    </div>
                </div>

                <div className="stat-card">
                    <div className="stat-icon orange">
                        <Clock size={24} />
                    </div>
                    <div className="stat-details">
                        <div className="stat-value">{stats.expired}</div>
                        <div className="stat-label">Expired Users</div>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="filters-section">
                <div className="search-box-modern">
                    <i className="fas fa-search"></i>
                    <input
                        type="text"
                        placeholder="Search users by name, email, or mobile..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="filter-select-modern"
                >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="expired">Expired</option>
                </select>
            </div>

            {/* Users Grid */}
            {loading ? (
                <div className="loading-modern">
                    <div className="spinner-modern"></div>
                    <p>Loading users...</p>
                </div>
            ) : filteredUsers.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon">
                        <UsersIcon size={40} />
                    </div>
                    <h3>No Users Found</h3>
                    <p className="text-gray-600">
                        {searchTerm || filterStatus !== 'all'
                            ? 'No users match your search criteria. Try adjusting your filters.'
                            : 'Create your first WiFi user to get started.'}
                    </p>
                    {!searchTerm && filterStatus === 'all' && (
                        <button onClick={handleNewUser} className="btn-primary-modern mt-4">
                            <Plus size={20} />
                            Create First User
                        </button>
                    )}
                </div>
            ) : (
                <div className="users-grid">
                    {filteredUsers.map((user) => (
                        <div key={user._id} className="user-card">
                            {/* Card Header */}
                            <div className="user-card-header">
                                <div className="user-avatar">
                                    <UsersIcon size={24} />
                                </div>
                                <div className="user-info">
                                    <h3 className="user-name">{user.name}</h3>
                                    <div className="user-plan">
                                        <Wifi size={14} />
                                        {user.planId?.name || 'No Plan'}
                                    </div>
                                </div>
                                <div className="user-actions">
                                    <button
                                        onClick={() => handleEdit(user)}
                                        className="action-btn edit"
                                        title="Edit user"
                                    >
                                        <Edit size={16} />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(user._id)}
                                        className="action-btn delete"
                                        title="Delete user"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </div>

                            {/* Card Body */}
                            <div className="user-card-body">
                                <div className="user-detail">
                                    <i className="fas fa-envelope"></i>
                                    <span>{user.email || 'No email'}</span>
                                </div>
                                <div className="user-detail">
                                    <i className="fas fa-phone"></i>
                                    <span>{user.mobile || 'No mobile'}</span>
                                </div>
                                <div className="user-detail">
                                    <i className="fas fa-building"></i>
                                    <span>{user.branchId?.name || 'No branch'}</span>
                                </div>
                            </div>

                            {/* Card Footer */}
                            <div className="user-card-footer">
                                <span
                                    className={`status-badge ${user.status}`}
                                >
                                    <span className="badge-dot"></span>
                                    {user.status}
                                </span>
                                <span className="user-date">
                                    <i className="far fa-calendar"></i>
                                    {formatDate(user.createdAt)}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                    <div className="modal-modern" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>{editingUser ? 'Edit User' : 'Create New User'}</h2>
                            <button onClick={() => setShowModal(false)} className="modal-close">
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="modal-body-modern">
                                <div className="form-grid">
                                    {/* Name */}
                                    <div className="form-group-modern">
                                        <label>Name *</label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            placeholder="Enter user name"
                                            required
                                        />
                                    </div>

                                    {/* Email */}
                                    <div className="form-group-modern">
                                        <label>Email</label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            placeholder="user@example.com"
                                        />
                                    </div>

                                    {/* Mobile */}
                                    <div className="form-group-modern">
                                        <label>Mobile *</label>
                                        <input
                                            type="tel"
                                            value={formData.mobile}
                                            onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                                            placeholder="Enter mobile number"
                                            required
                                        />
                                    </div>

                                    {/* Password */}
                                    <div className="form-group-modern">
                                        <label>Password {!editingUser && '*'}</label>
                                        <input
                                            type="password"
                                            value={formData.password}
                                            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                            placeholder={editingUser ? 'Leave blank to keep current' : 'Enter password'}
                                            required={!editingUser}
                                        />
                                    </div>

                                    {/* Branch */}
                                    <div className="form-group-modern">
                                        <label>Branch *</label>
                                        <select
                                            value={formData.branchId}
                                            onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                                            required
                                        >
                                            <option value="">Select branch</option>
                                            {branches.map(branch => (
                                                <option key={branch._id} value={branch._id}>
                                                    {branch.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Plan */}
                                    <div className="form-group-modern">
                                        <label>Plan *</label>
                                        <select
                                            value={formData.planId}
                                            onChange={(e) => setFormData({ ...formData, planId: e.target.value })}
                                            required
                                        >
                                            <option value="">Select plan</option>
                                            {plans.map(plan => (
                                                <option key={plan._id} value={plan._id}>
                                                    {plan.name} - {plan.price} ({plan.validity} days)
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Status */}
                                    <div className="form-group-modern full-width">
                                        <label>Status</label>
                                        <select
                                            value={formData.status}
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                        >
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                            <option value="expired">Expired</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer-modern">
                                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary-modern">
                                    Cancel
                                </button>
                                <button type="submit" className="btn-primary-modern">
                                    <Plus size={20} />
                                    {editingUser ? 'Update User' : 'Create User'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-modern modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern danger">
                            <h2>Delete User</h2>
                            <button onClick={() => setShowDeleteModal(false)} className="modal-close">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="modal-body-modern">
                            <p>Are you sure you want to delete this user? This action cannot be undone.</p>
                        </div>

                        <div className="modal-footer-modern">
                            <button onClick={() => setShowDeleteModal(false)} className="btn-secondary-modern">
                                Cancel
                            </button>
                            <button onClick={confirmDelete} className="btn-danger-modern">
                                <Trash2 size={20} />
                                Delete User
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Users;
